import { LocalizationManager, useLocalization } from '@lib/localization';
import { Button, ListSubheader, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { buttonStyles } from '@pages/CustomPoiTypes.styles';
import { useGetRecentInspectionsQuery } from '@store/api/statisticEndpoints';
import { useState } from 'react';
import { SyncIcon } from 'react-line-awesome';
import { localeFieldStyles, sourceFieldStyles, sourceOptionStyles, sourceRowStyles } from './PdfPreview.styles';
import { type PreviewInspection, type PreviewSourceControl } from './usePreviewSource';

interface Props {
  control: PreviewSourceControl;
  /** True where a choice made here would draw nothing: the preview it stands in asks for nothing any more. */
  disabled?: boolean;
}

// A select holds strings: the sample has no id, an inspection is named by its project and its own.
const SAMPLE = 'sample';

const valueOf = (inspection: PreviewInspection): string => `${inspection.projectId}/${inspection.inspectionId}`;

/**
 * How many of the newest inspections the picker offers. A short list to pick from, not a
 * way to find an inspection: it has no search and no further page.
 */
const RECENT = 10;

/** A language's name in that language, as the Settings page lists them. */
const languageName = (code: string): string => {
  try {
    return new Intl.DisplayNames([code], { type: 'language' }).of(code) ?? code;
  } catch (_) {
    return code;
  }
};

/**
 * What a preview is drawn from: the built-in sample or one of the reader's newest
 * inspections, and the language of the report. Goes into `PdfPreview`'s `toolbar`, with
 * `usePreviewSource` holding its state.
 *
 * The sample is the default because it is small and shows every kind of content. A real
 * inspection is drawn only when Refresh is pressed, which is why the button stands out
 * while there is something it would change.
 */
const PreviewSource: React.FC<Props> = ({ control, disabled = false }) => {
  const { t } = useLocalization();
  const { inspection, locale, pending } = control;

  // The inspections are read when the picker is first opened: most previews stay on the sample.
  const [opened, setOpened] = useState(false);
  const { data: recent } = useGetRecentInspectionsQuery({ page: 1, limit: RECENT }, { skip: !opened });

  // The chosen one stays on offer whatever the list holds: a select cannot show a value
  // none of its entries has.
  const listed: PreviewInspection[] = (recent?.data ?? []).map(({ projectId, inspectionId, name, projectName }) => ({
    projectId, inspectionId, name, projectName,
  }));
  const inspections = inspection && !listed.some((candidate) => valueOf(candidate) === valueOf(inspection))
    ? [inspection, ...listed]
    : listed;

  // The interface's own languages: the report is downloaded in the one the reader uses.
  const languages = [...new Set([...LocalizationManager.supportedLanguageCodes, locale])];

  return (
    <Stack direction="row" sx={sourceRowStyles}>
      <TextField
        select
        size="small"
        label={t('ReportPreview_Data')}
        disabled={disabled}
        value={inspection ? valueOf(inspection) : SAMPLE}
        onChange={(event) => control.chooseInspection(
          inspections.find((candidate) => valueOf(candidate) === event.target.value) ?? null,
        )}
        slotProps={{
          select: {
            onOpen: () => setOpened(true),
            // An entry is two lines in the menu; the closed field shows the name alone.
            renderValue: () => inspection?.name ?? t('ReportPreview_Sample'),
          },
        }}
        sx={sourceFieldStyles}
      >
        <MenuItem value={SAMPLE}>{t('ReportPreview_Sample')}</MenuItem>
        {inspections.length > 0 && <ListSubheader>{t('Statistic_RecentInspections')}</ListSubheader>}
        {inspections.map((candidate) => (
          <MenuItem key={valueOf(candidate)} value={valueOf(candidate)} sx={sourceOptionStyles}>
            <Typography variant="body2">{candidate.name}</Typography>
            <Typography variant="caption" color="text.secondary">{candidate.projectName}</Typography>
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        label={t('Settings_Language')}
        disabled={disabled}
        value={locale}
        onChange={(event) => control.chooseLocale(event.target.value)}
        sx={localeFieldStyles}
      >
        {languages.map((code) => <MenuItem key={code} value={code}>{languageName(code)}</MenuItem>)}
      </TextField>

      {/* No hint on a button that is switched off: it fires no event for one to be shown on. */}
      <Tooltip title={inspection && !disabled ? t('ReportPreview_RefreshHint') : ''}>
        <Button
          size="small"
          variant={pending ? 'contained' : 'text'}
          color={pending ? 'primary' : 'inherit'}
          disableElevation
          disabled={disabled}
          startIcon={<SyncIcon />}
          onClick={control.refresh}
          sx={buttonStyles}
        >
          {t('ReportPreview_Refresh')}
        </Button>
      </Tooltip>
    </Stack>
  );
};

export default PreviewSource;
